import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';
import ShopItemDto, { ShopDisplaySection } from '../models/dtos/ShopItemDto';
import { isNewShopSku } from '../utils/shopItemPayload';
import {
    buildShopLayoutPreview,
    DEFAULT_SHOP_PREVIEW_FILTERS,
    filterShopItemsForPreview,
    itemPreviewLabel,
    moveSectionInOrder,
    normalizeSectionDisplayOrder,
    rowWidthHint,
    ShopPreviewCatalogFilter,
    ShopPreviewFilters,
    ShopPreviewSection,
    ShopPreviewStatusFilter,
    sortShopItemsByDisplayOrder,
    SHOP_SECTION_ORDER_FALLBACK,
} from '../utils/shopLayoutPreview';
import { shopHtmlCardHeight } from '../utils/shopHtmlInjector';

const PHONE_WIDTH = 340;

const SECTION_ACCENT: Record<string, string> = {
    FIRST_PURCHASE: '#D4A056',
    DAILY_DEALS: '#FFB84D',
    BUNDLES: '#CD7F32',
    COIN_PACKS: '#7EB6FF',
    FREE_REWARDS: '#FFB03A',
};

export type ShopLayoutItemPatch = Partial<
    Pick<ShopItemDto, 'displaySection' | 'displayOrder' | 'columnsInRow'>
>;

interface ShopLayoutPreviewProps {
    items: ShopItemDto[];
    sectionDisplayOrder: ShopDisplaySection[];
    onSectionDisplayOrderChange: (order: ShopDisplaySection[]) => void;
    /** Apply local layout field changes (keeps unsaved drafts in parent). */
    onItemPatch: (id: number, patch: ShopLayoutItemPatch) => void;
    /** Persist one shop item (layout fields). */
    onSaveItem: (item: ShopItemDto) => Promise<boolean>;
    /** Persist category order (including Free Rewards / video). */
    onSaveSectionOrder: () => Promise<boolean>;
    dirtyItemIds: Set<number>;
    sectionOrderDirty: boolean;
    saving?: boolean;
}

function PreviewCard({
    item,
    half,
    selected,
    dirty,
    onSelect,
}: {
    item: ShopItemDto;
    half: boolean;
    selected: boolean;
    dirty: boolean;
    onSelect: () => void;
}) {
    const section = item.displaySection ?? 'COIN_PACKS';
    const width = half ? (PHONE_WIDTH - 28) / 2 : PHONE_WIDTH - 24;
    const height = Math.min(shopHtmlCardHeight(section, width), half ? 132 : 92);
    const accent = SECTION_ACCENT[section] ?? '#888';
    const isVideo = item.id === -1;

    return (
        <Box
            sx={{
                width: half ? '50%' : '100%',
                px: 0.5,
                boxSizing: 'border-box',
            }}
        >
            <Box
                onClick={onSelect}
                sx={{
                    height,
                    borderRadius: 2,
                    border: `2px solid ${selected ? '#fff' : accent}`,
                    outline: selected ? '2px solid #FFBE3B' : 'none',
                    outlineOffset: 1,
                    cursor: 'pointer',
                    background: isVideo
                        ? 'linear-gradient(165deg, rgba(40,32,18,0.98), rgba(18,14,10,0.99))'
                        : item.isDeleted
                            ? 'linear-gradient(165deg, rgba(40,28,28,0.95), rgba(20,14,14,0.98))'
                            : 'linear-gradient(165deg, rgba(28,28,34,0.98), rgba(14,14,18,0.99))',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 1,
                    overflow: 'hidden',
                    opacity: item.isDeleted ? 0.72 : 1,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: '#F9FAFD',
                        fontWeight: 700,
                        lineHeight: 1.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {itemPreviewLabel(item)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                        label={half ? '½ row' : 'full'}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(255,255,255,0.08)', color: '#ddd' }}
                    />
                    <Chip
                        label={`ord ${item.displayOrder ?? 0}`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(255,255,255,0.08)', color: '#ddd' }}
                    />
                    {dirty ? (
                        <Chip
                            label="unsaved"
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: 10 }}
                        />
                    ) : null}
                    {item.isDeleted ? (
                        <Chip label="off" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                </Stack>
            </Box>
        </Box>
    );
}

function PreviewSectionBlock({
    section,
    selectedId,
    dirtyItemIds,
    onSelect,
    onMoveSection,
    canMoveUp,
    canMoveDown,
}: {
    section: ShopPreviewSection;
    selectedId: number | null;
    dirtyItemIds: Set<number>;
    onSelect: (item: ShopItemDto) => void;
    onMoveSection: (direction: -1 | 1) => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
}) {
    const accent = SECTION_ACCENT[section.type] ?? '#888';
    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75, px: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />
                <Typography variant="subtitle2" sx={{ color: '#F9FAFD', fontWeight: 700, flex: 1 }}>
                    {section.title}
                </Typography>
                <Tooltip title="Move category up">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!canMoveUp}
                            onClick={() => onMoveSection(-1)}
                            sx={{ color: '#ccc' }}
                        >
                            <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Move category down">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!canMoveDown}
                            onClick={() => onMoveSection(1)}
                            sx={{ color: '#ccc' }}
                        >
                            <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            {section.rows.map((row) => (
                <Box key={row.key} sx={{ mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', px: 0.5, mb: 0.25 }}>
                        {rowWidthHint(row)}
                    </Typography>
                    <Stack direction="row" sx={{ width: '100%' }}>
                        {row.items.map((item) => (
                            <PreviewCard
                                key={item.id}
                                item={item}
                                half={row.columns === 2}
                                selected={selectedId === item.id}
                                dirty={dirtyItemIds.has(item.id)}
                                onSelect={() => onSelect(item)}
                            />
                        ))}
                        {row.columns === 2 && row.items.length === 1 ? (
                            <Box sx={{ width: '50%', px: 0.5 }} />
                        ) : null}
                    </Stack>
                </Box>
            ))}
        </Box>
    );
}

/**
 * Interactive shop layout workspace: filter, preview, and edit display fields in place.
 */
export default function ShopLayoutPreview({
    items,
    sectionDisplayOrder,
    onSectionDisplayOrderChange,
    onItemPatch,
    onSaveItem,
    onSaveSectionOrder,
    dirtyItemIds,
    sectionOrderDirty,
    saving = false,
}: ShopLayoutPreviewProps) {
    const navigate = useNavigate();
    const [filters, setFilters] = React.useState<ShopPreviewFilters>(DEFAULT_SHOP_PREVIEW_FILTERS);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [actionMessage, setActionMessage] = React.useState<string | null>(null);
    const [actionError, setActionError] = React.useState(false);

    const filteredItems = React.useMemo(
        () => filterShopItemsForPreview(items, filters),
        [items, filters],
    );

    const normalizedSectionOrder = React.useMemo(
        () => normalizeSectionDisplayOrder(sectionDisplayOrder),
        [sectionDisplayOrder],
    );

    const sections = React.useMemo(
        () => buildShopLayoutPreview(filteredItems, {
            includeVideoSlot: filters.status !== 'disabled'
                && (filters.section === 'ALL' || filters.section === 'FREE_REWARDS'),
            sectionDisplayOrder: normalizedSectionOrder,
        }),
        [filteredItems, filters.status, filters.section, normalizedSectionOrder],
    );

    const selectedItem = React.useMemo(() => {
        if (selectedId == null) return null;
        if (selectedId === -1) {
            return {
                id: -1,
                betcoins: 0,
                type: 'Bonus',
                effectDuration: 0,
                noAds: false,
                isDeleted: false,
                osType: 'all',
                displaySection: 'FREE_REWARDS' as ShopDisplaySection,
                displayOrder: 0,
                columnsInRow: 1,
                titleText: 'Video reward',
                storeCode: 'video_reward',
            } satisfies ShopItemDto;
        }
        return items.find((i) => i.id === selectedId) ?? null;
    }, [selectedId, items]);

    const sectionSiblings = React.useMemo(() => {
        if (!selectedItem || selectedItem.id === -1) return [] as ShopItemDto[];
        const section = selectedItem.displaySection ?? 'COIN_PACKS';
        return sortShopItemsByDisplayOrder(
            filteredItems.filter((i) => (i.displaySection ?? 'COIN_PACKS') === section),
        );
    }, [selectedItem, filteredItems]);

    const selectedIndex = selectedItem
        ? sectionSiblings.findIndex((i) => i.id === selectedItem.id)
        : -1;

    const patchFilters = (patch: Partial<ShopPreviewFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    };

    const moveSelected = (direction: -1 | 1) => {
        if (!selectedItem || selectedItem.id === -1 || selectedIndex < 0) return;
        const other = sectionSiblings[selectedIndex + direction];
        if (!other) return;
        const orderSelected = selectedItem.displayOrder ?? 0;
        const orderOther = other.displayOrder ?? 0;
        if (orderSelected === orderOther) {
            onItemPatch(selectedItem.id, {
                displayOrder: direction < 0 ? orderOther - 1 : orderOther + 1,
            });
            return;
        }
        onItemPatch(selectedItem.id, { displayOrder: orderOther });
        onItemPatch(other.id, { displayOrder: orderSelected });
    };

    const saveSelected = async () => {
        setActionMessage(null);
        if (!selectedItem) return;
        if (selectedItem.id === -1) {
            const ok = await onSaveSectionOrder();
            setActionError(!ok);
            setActionMessage(ok ? 'Saved category order (incl. video).' : 'Failed to save category order.');
            return;
        }
        const ok = await onSaveItem(selectedItem);
        setActionError(!ok);
        setActionMessage(ok ? `Saved layout for ${selectedItem.storeCode}.` : 'Failed to save item.');
    };

    const saveAllDirty = async () => {
        setActionMessage(null);
        let failed = 0;
        let saved = 0;
        for (const id of Array.from(dirtyItemIds)) {
            const item = items.find((i) => i.id === id);
            if (!item) continue;
            const ok = await onSaveItem(item);
            if (ok) saved += 1;
            else failed += 1;
        }
        if (sectionOrderDirty) {
            const ok = await onSaveSectionOrder();
            if (ok) saved += 1;
            else failed += 1;
        }
        setActionError(failed > 0);
        setActionMessage(
            failed > 0
                ? `Saved ${saved}, failed ${failed}.`
                : `Saved ${saved} change(s).`,
        );
    };

    return (
        <Stack spacing={2} width="100%">
            <Typography variant="body2" color="text.secondary">
                Use ↑↓ on each category to reorder sections (including Video). Click a card to edit item
                section / order / row width. Save all before checking the app.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={filters.status}
                        onChange={(e) => patchFilters({ status: e.target.value as ShopPreviewStatusFilter })}
                    >
                        <MenuItem value="active">Active only</MenuItem>
                        <MenuItem value="disabled">Disabled only</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Catalog</InputLabel>
                    <Select
                        label="Catalog"
                        value={filters.catalog}
                        onChange={(e) => patchFilters({ catalog: e.target.value as ShopPreviewCatalogFilter })}
                    >
                        <MenuItem value="new_shop">Shop v2 (new_shop_*)</MenuItem>
                        <MenuItem value="legacy">Legacy SKUs</MenuItem>
                        <MenuItem value="all">All SKUs</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>OS</InputLabel>
                    <Select
                        label="OS"
                        value={filters.osType}
                        onChange={(e) => patchFilters({ osType: e.target.value })}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Android">Android</MenuItem>
                        <MenuItem value="iOS">iOS</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Section</InputLabel>
                    <Select
                        label="Section"
                        value={filters.section}
                        onChange={(e) =>
                            patchFilters({ section: e.target.value as ShopPreviewFilters['section'] })
                        }
                    >
                        <MenuItem value="ALL">All sections</MenuItem>
                        {SHOP_SECTION_ORDER_FALLBACK.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Chip label={`${filteredItems.length} in preview`} size="small" variant="outlined" />
                {(dirtyItemIds.size > 0 || sectionOrderDirty) ? (
                    <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                        disabled={saving}
                        onClick={saveAllDirty}
                    >
                        Save all ({dirtyItemIds.size + (sectionOrderDirty ? 1 : 0)})
                    </Button>
                ) : null}
            </Stack>

            {actionMessage ? (
                <Alert severity={actionError ? 'error' : 'success'} onClose={() => setActionMessage(null)}>
                    {actionMessage}
                </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
                <Box sx={{ display: 'flex', justifyContent: 'center', width: { xs: '100%', lg: 'auto' } }}>
                    <Box
                        sx={{
                            width: PHONE_WIDTH + 16,
                            borderRadius: '28px',
                            border: '3px solid #333',
                            bgcolor: '#0E0F12',
                            p: 1,
                            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                        }}
                    >
                        <Box sx={{ height: 18, borderRadius: '12px 12px 0 0', mb: 1, bgcolor: '#1a1a22' }} />
                        <Box
                            sx={{
                                maxHeight: 640,
                                overflowY: 'auto',
                                px: 0.25,
                                pb: 1,
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: '#333', borderRadius: 3 },
                            }}
                        >
                            {sections.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 4 }}>
                                    No items match these preview filters.
                                </Typography>
                            ) : (
                                sections.map((section, index) => (
                                    <PreviewSectionBlock
                                        key={section.type}
                                        section={section}
                                        selectedId={selectedId}
                                        dirtyItemIds={dirtyItemIds}
                                        onSelect={(item) => setSelectedId(item.id)}
                                        canMoveUp={index > 0}
                                        canMoveDown={index < sections.length - 1}
                                        onMoveSection={(direction) => {
                                            onSectionDisplayOrderChange(
                                                moveSectionInOrder(
                                                    normalizedSectionOrder,
                                                    section.type,
                                                    direction,
                                                    sections.map((s) => s.type),
                                                ),
                                            );
                                        }}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, minWidth: 280, width: '100%' }}>
                    <Typography variant="h6" gutterBottom>Selected item</Typography>
                    {!selectedItem ? (
                        <Alert severity="info">Select a card in the phone preview to edit layout fields.</Alert>
                    ) : selectedItem.id === -1 ? (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight={700}>Video reward (Free Rewards)</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Use the ↑↓ arrows on the Free Rewards category in the phone preview to move video
                                among shop sections, then save.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={saving || !sectionOrderDirty}
                                    onClick={saveSelected}
                                >
                                    Save category order
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {selectedItem.storeCode}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={isNewShopSku(selectedItem) ? 'new_shop' : 'legacy'}
                                    color={isNewShopSku(selectedItem) ? 'warning' : 'default'}
                                />
                                <Chip
                                    size="small"
                                    label={selectedItem.isDeleted ? 'disabled' : 'active'}
                                    color={selectedItem.isDeleted ? 'error' : 'success'}
                                />
                                {dirtyItemIds.has(selectedItem.id) ? (
                                    <Chip size="small" label="unsaved" color="warning" />
                                ) : null}
                            </Stack>

                            <FormControl fullWidth size="small">
                                <InputLabel>Display section</InputLabel>
                                <Select
                                    label="Display section"
                                    value={selectedItem.displaySection ?? 'COIN_PACKS'}
                                    onChange={(e) =>
                                        onItemPatch(selectedItem.id, {
                                            displaySection: e.target.value as ShopDisplaySection,
                                        })
                                    }
                                >
                                    {SHOP_SECTION_ORDER_FALLBACK.filter((s) => s !== 'FREE_REWARDS').map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    type="number"
                                    label="Display order"
                                    size="small"
                                    sx={{ width: 140 }}
                                    value={selectedItem.displayOrder ?? 0}
                                    onChange={(e) =>
                                        onItemPatch(selectedItem.id, {
                                            displayOrder: parseInt(e.target.value, 10) || 0,
                                        })
                                    }
                                />
                                <Tooltip title="Move up in section">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={selectedIndex <= 0}
                                            onClick={() => moveSelected(-1)}
                                        >
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Move down in section">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={selectedIndex < 0 || selectedIndex >= sectionSiblings.length - 1}
                                            onClick={() => moveSelected(1)}
                                        >
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>

                            <FormControl fullWidth size="small">
                                <InputLabel>Row width</InputLabel>
                                <Select
                                    label="Row width"
                                    value={(selectedItem.columnsInRow ?? 1) >= 2 ? 2 : 1}
                                    onChange={(e) =>
                                        onItemPatch(selectedItem.id, {
                                            columnsInRow: Number(e.target.value) === 2 ? 2 : 1,
                                        })
                                    }
                                >
                                    <MenuItem value={1}>Full width (1 per row)</MenuItem>
                                    <MenuItem value={2}>Half width (2 per row)</MenuItem>
                                </Select>
                            </FormControl>

                            <Divider />

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={saving || !dirtyItemIds.has(selectedItem.id)}
                                    onClick={saveSelected}
                                >
                                    Save layout
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => navigate(`/shop-items/${selectedItem.id}/edit`)}
                                >
                                    Full editor
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Stack>
    );
}
